import React from "react";
import {
  TextInput,
  required,
  maxLength,
  ReferenceInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  SelectInput,
  BooleanInput,
  NumberInput,
  useLoading,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { Grid } from "@mui/material";
import SecuredComponent from "server/security/SecuredComponent";
import { tenants } from "utils/tenants";
import { documentRequests } from "api/documentsApi";
import { administrationRequests } from "api/administrationApi";
import { SpecialityStatusChoices } from "utils/choices";

const PROFESSIONAL_QUALIFICATION_MAX_LENGTH = 200;

const SaveForm = () => {
  const loading = useLoading();
  const professionalQualificationBg = useWatch({ name: "professionalQualification.nameBg" });
  const professionalQualificationEn = useWatch({ name: "professionalQualification.nameEn" });
  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        md={6}
        xs={12}
      >
        <TextInput
          label="Име на български език"
          source="nameBg"
          validate={required()}
          readOnly={loading}
        />
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <TextInput
          label="Име на английски език"
          source="nameEn"
          validate={required()}
          readOnly={loading}
        />
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <TextInput
          label="Професионална квалификация (BG)"
          source="professionalQualification.nameBg"
          validate={[required(), maxLength(PROFESSIONAL_QUALIFICATION_MAX_LENGTH)]}
          inputProps={{ maxLength: PROFESSIONAL_QUALIFICATION_MAX_LENGTH }}
          helperText={`${professionalQualificationBg?.length || 0}/${PROFESSIONAL_QUALIFICATION_MAX_LENGTH}`}
          readOnly={loading}
        />
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <TextInput
          label="Професионална квалификация (EN)"
          source="professionalQualification.nameEn"
          validate={[required(), maxLength(PROFESSIONAL_QUALIFICATION_MAX_LENGTH)]}
          inputProps={{ maxLength: PROFESSIONAL_QUALIFICATION_MAX_LENGTH }}
          helperText={`${professionalQualificationEn?.length || 0}/${PROFESSIONAL_QUALIFICATION_MAX_LENGTH}`}
          readOnly={loading}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceInput
          source="structuralUnitId"
          reference={administrationRequests.getStructuralUnitsSelect()}
          filter={{ filterByUser: true }}
        >
          <SelectInput
            label="Структурна единица"
            validate={required()}
            readOnly={loading}
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceInput
          source="professionalDirectionId"
          reference={administrationRequests.getProfessionalDirectionsSelect()}
        >
          <SelectInput
            label="Професионално направление"
            optionText="nameBg"
            validate={required()}
            readOnly={loading}
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceArrayInput
          source="studyTypeIds"
          reference={administrationRequests.getStudyTypesSelect()}
        >
          <AutocompleteArrayInput
            optionText="name"
            label="Тип обучение"
            fullWidth
            validate={required()}
            readOnly={loading}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceArrayInput
          source="admissionAfterEducationLevelIds"
          reference={administrationRequests.getAdmissionAfterEducationLevelsSelect()}
        >
          <AutocompleteArrayInput
            optionText="name"
            label="Прием след"
            fullWidth
            validate={required()}
            readOnly={loading}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          label="Код"
          source="code"
          readOnly={loading}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceArrayInput
          source="receptionTypesIds"
          reference={administrationRequests.getReceptionTypesSelect()}
        >
          <AutocompleteArrayInput
            optionText="name"
            label="Типове прием"
            fullWidth
            readOnly={loading}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceArrayInput
          source="instructionLanguageIds"
          label="Езици на преподаване"
          reference={administrationRequests.getInstructionLanguagesSelect()}
        >
          <AutocompleteArrayInput
            optionText="name"
            label="Език на преподаване"
            fullWidth
            readOnly={loading}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <ReferenceArrayInput
          source="examsIds"
          reference={administrationRequests.getExamsSelect()}
        >
          <AutocompleteArrayInput
            optionText="name"
            label="Изпити"
            fullWidth
            readOnly={loading}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <ReferenceArrayInput
          source="declarationIds"
          reference={documentRequests.getDeclarationsSelect()}
        >
          <AutocompleteArrayInput
            optionText="name"
            label="Декларации"
            fullWidth
            readOnly={loading}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <ReferenceArrayInput
          source="specificRequirementIds"
          reference={administrationRequests.getSpecificRequirementsSelect()}
        >
          <AutocompleteArrayInput
            optionText="name"
            label="Специфични изисквания"
            fullWidth
            readOnly={loading}
          />
        </ReferenceArrayInput>
      </Grid>
      <Grid
        item
        md={6}
        xs={12}
      >
        <NumberInput
          label="Минимален бал за прием"
          source="minimalScoreForEntry"
          readOnly={loading}
          // validate={required()}
        />
      </Grid>

      <Grid
        item
        md={6}
        xs={12}
      >
        <SelectInput
          label="Статус"
          source="status"
          choices={SpecialityStatusChoices}
          validate={required()}
          readOnly={loading}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <BooleanInput
          label="Предлага се за чужденци"
          source={"isOfferedToForeigners"}
          readOnly={loading}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <BooleanInput
          label="Полово разпределение на местата"
          source={"isDistributedByGender"}
          readOnly={loading}
        />
      </Grid>
      <SecuredComponent tenancies={tenants.MU}>
        <Grid
          item
          xs={12}
          md={6}
        >
          <BooleanInput
            label="Класиране само по първо желание"
            source={"firstChoiceRankingOnly"}
            readOnly={loading}
          />
        </Grid>
      </SecuredComponent>
      <Grid
        item
        xs={12}
        md={6}
      >
        <BooleanInput
          label="Защитена специалност"
          source={"isProtected"}
          readOnly={loading}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <BooleanInput
          label="Задължителен избор на чужд език"
          source={"canPickForeignLanguage "}
          readOnly={loading}
        />
      </Grid>
    </Grid>
  );
};

export default SaveForm;
